/**
 * Color utilities for the edges design system
 * Provides theme-aware color resolution and management
 */

// Cache for resolved CSS variable colors
const colorCache = new Map<string, string>();

/**
 * Type for Mapbox GL JS expressions
 * Represents nested array structures used by Mapbox for data-driven styling
 */
export type MapboxExpression = string | number | boolean | Array<string | number | boolean | MapboxExpression>;

/**
 * Maximum alias-chain depth to follow when a CSS custom property's declared
 * value is itself a `var(...)` reference. Edges tokens commonly alias
 * theme-specific names to base names (e.g. in dark theme
 * `--color-feedback-success-border: var(--color-success-base)`). We must
 * follow those chains to land on a Mapbox-parseable color; the cap prevents
 * runaway recursion if a token cycles or chains pathologically.
 */
const MAX_VAR_ALIAS_DEPTH = 8;

/**
 * Strip a `var(--name, fallback?)` wrapper down to just the inner `--name`.
 * Returns the input unchanged if it isn't a var() reference. We intentionally
 * drop the in-CSS fallback expression: at this layer we want to resolve the
 * declared chain, and our own `fallback` parameter handles the terminal miss.
 */
function unwrapVar(value: string): string {
  if (!value.startsWith("var(")) return value;
  // Take everything between the first `(` and the LAST `)` so nested
  // expressions like `var(--a, var(--b))` strip down to `--a, var(--b)`,
  // then trim to just the first identifier.
  const inner = value.slice(4, value.lastIndexOf(")")).trim();
  const comma = inner.indexOf(",");
  return (comma === -1 ? inner : inner.slice(0, comma)).trim();
}

/**
 * Resolves a CSS variable to its computed color value.
 * Supports multiple input formats, automatically adds the `color-` prefix
 * when needed, and follows `var(--alias)` chains across themes.
 *
 * @param variableName - CSS variable name in any of these formats:
 *   - "action-brand" → resolves to --color-action-brand
 *   - "color-action-brand" → resolves to --color-action-brand
 *   - "--color-action-brand" → used as-is
 *   - "var(--color-action-brand)" → unwrapped and used
 * @returns Resolved color value (hex / rgb / named) or `fallback`
 *
 * Why alias following: `getComputedStyle().getPropertyValue()` returns the
 * DECLARED value of a custom property, not its fully-resolved value. In
 * Edges tokens, dark-theme feedback colors are declared as
 * `var(--color-*-base)`, so a single lookup hands back another `var(...)`
 * string. Mapbox GL can't parse that — the caller would either drop the
 * paint property or fall back to a default. We follow the chain (capped at
 * `MAX_VAR_ALIAS_DEPTH`) so callers always receive a parseable color.
 */
export const getResolvedColor = (variableName: string, fallback = "#000000"): string => {
  // Early return if running in non-browser environment
  if (typeof window === "undefined") return fallback;
  return resolveCssVar(variableName, fallback, 0);
};

function resolveCssVar(variableName: string, fallback: string, depth: number): string {
  // Cap recursion: alias chains in Edges tokens are 1–2 deep in practice;
  // anything beyond MAX_VAR_ALIAS_DEPTH is almost certainly a cycle.
  if (depth > MAX_VAR_ALIAS_DEPTH) return fallback;

  // Normalize the variable name
  let varName = variableName.startsWith("var(")
    ? unwrapVar(variableName)
    : variableName.startsWith("--")
      ? variableName
      : `--${variableName}`;

  // Add color- prefix ONLY for shorthand forms without the -- prefix
  // This allows "action-brand" to resolve to "--color-action-brand"
  // but preserves exact variable names like "--text-body" or "--neutral-white"
  if (!variableName.startsWith("--") && !variableName.startsWith("var(") && !varName.startsWith("--color-")) {
    varName = `--color-${varName.slice(2)}`;
  }

  // Return cached value if available. The cache always holds a terminal
  // (fully-resolved) value because we only cache after following the chain.
  if (colorCache.has(varName)) {
    return colorCache.get(varName)!;
  }

  try {
    // Get computed style from body element (where theme classes are applied)
    // This ensures we pick up theme-specific variables like .theme-dark
    const root = document.body || document.documentElement;
    const computedStyle = getComputedStyle(root);
    const value = computedStyle.getPropertyValue(varName).trim();

    if (value) {
      // If the declared value is itself a var() reference (theme aliasing —
      // e.g. dark `--color-feedback-success-border: var(--color-success-base)`)
      // follow the chain. Without this, Mapbox would receive a literal
      // `var(...)` string and reject the paint.
      if (value.startsWith("var(")) {
        const resolved = resolveCssVar(value, fallback, depth + 1);
        colorCache.set(varName, resolved);
        return resolved;
      }
      // Cache the terminal value
      colorCache.set(varName, value);
      return value;
    }

    // Try alternative method with temporary element. The CSS engine fully
    // resolves var() chains here, so this is a natural belt-and-suspenders
    // path when getPropertyValue comes back empty.
    const tempElement = document.createElement("div");
    tempElement.style.color = `var(${varName})`;
    document.body.appendChild(tempElement);
    const resolvedColor = getComputedStyle(tempElement).color;
    document.body.removeChild(tempElement);

    if (resolvedColor && resolvedColor !== "rgb(0, 0, 0)") {
      colorCache.set(varName, resolvedColor);
      return resolvedColor;
    }

    return fallback;
  } catch (error) {
    console.warn(`Failed to resolve color for ${variableName}:`, error);
    return fallback;
  }
}

/**
 * Clears the color cache - useful when theme changes
 */
export const clearColorCache = () => {
  colorCache.clear();
};

/**
 * Gets theme colors from CSS variables
 * Returns an array of resolved color values
 */
export const getThemeCategoricalColors = (): string[] => {
  const colors: string[] = [];

  // Try to get categorical colors from CSS variables
  for (let i = 1; i <= 12; i++) {
    const color = getResolvedColor(`--color-viz-categorical-${i}`, "");
    if (color) {
      colors.push(color);
    }
  }

  // Fallback to default palette if no theme colors found
  if (colors.length === 0) {
    return getDefaultColors();
  }

  return colors;
};

/**
 * Default color palette matching the edges design system
 */
export const getDefaultColors = (): string[] => [
  "#5424db", // viz-categorical-1 — Deep Violet
  "#d92361", // viz-categorical-2 — Crimson
  "#e86a00", // viz-categorical-3 — Burnt Orange
  "#5d89ff", // viz-categorical-4 — Cornflower
  "#9a47e2", // viz-categorical-5 — Purple
  "#f24cb8", // viz-categorical-6 — Hot Pink
  "#0ba286", // viz-categorical-7 — Teal
  "#9c28af", // viz-categorical-8 — Purple-Magenta
  "#036e45", // viz-categorical-9 — Forest Green
  "#52a119", // viz-categorical-10 — Lime Green
  "#ff513d", // viz-categorical-11 — Red-Orange
  "#046691", // viz-categorical-12 — Deep Cyan
];

/**
 * Gets the default chart color from theme
 */
export const getDefaultChartColor = (): string => {
  return getResolvedColor("--color-viz-default", "#444ae1");
};

/**
 * Maps categories to colors based on a color scheme
 */
export const createCategoryColorMap = (
  categories: string[],
  customColors?: Record<string, string>
): Record<string, string> => {
  const colorMap: Record<string, string> = {};
  const themeColors = getThemeCategoricalColors();

  categories.forEach((category, index) => {
    if (customColors && customColors[category]) {
      // Use custom color if provided
      colorMap[category] = customColors[category];
    } else {
      // Use theme color based on index
      colorMap[category] = themeColors[index % themeColors.length];
    }
  });

  return colorMap;
};

/**
 * Determines if a color is light or dark
 * Useful for determining text color on colored backgrounds
 */
export const isLightColor = (color: string): boolean => {
  // Convert to RGB
  const hex = color.trim().match(/^#([\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i)?.[1];
  const channels = hex
    ? (hex.length <= 4
        ? hex
            .slice(0, 3)
            .split("")
            .map((digit) => digit + digit)
        : hex.slice(0, 6).match(/../g)!
      ).map((channel) => Number.parseInt(channel, 16))
    : color.match(/\d+/g)?.map(Number);
  if (!channels || channels.length < 3) return true;

  // Calculate luminance
  const [r, g, b] = channels;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5;
};

/**
 * Gets contrasting text color for a background
 */
export const getContrastingTextColor = (backgroundColor: string): string => {
  if (isLightColor(backgroundColor)) {
    return getResolvedColor("--color-text-primary", "#333333");
  }
  return getResolvedColor("--color-text-inverse", "#ffffff");
};

/**
 * Extracts unique values for a given property from GeoJSON FeatureCollection
 *
 * @param data - GeoJSON FeatureCollection or Feature
 * @param property - Property name to extract values from
 * @returns Array of unique values, sorted
 * @throws Warning if data structure is invalid
 */
export const extractUniqueValues = (
  data: GeoJSON.FeatureCollection | GeoJSON.Feature,
  property: string
): (string | number)[] => {
  // Validate data structure
  if (!data || typeof data !== "object") {
    console.warn("[Colors] Invalid GeoJSON data provided to extractUniqueValues");
    return [];
  }

  // Validate it's GeoJSON
  if (!("type" in data) || (data.type !== "FeatureCollection" && data.type !== "Feature")) {
    console.warn("[Colors] Data is not valid GeoJSON (missing type or invalid type)");
    return [];
  }

  const values = new Set<string | number>();

  // Get features array
  const features = "features" in data ? data.features : [data];

  // Validate features exist
  if (!Array.isArray(features) || features.length === 0) {
    console.warn(`[Colors] No features found in GeoJSON data`);
    return [];
  }

  // Extract values
  let propertyFoundCount = 0;
  features.forEach((feature) => {
    if (!feature || typeof feature !== "object") {
      return; // Skip invalid features
    }

    if (feature.properties && property in feature.properties) {
      propertyFoundCount++;
      const value = feature.properties[property];
      if (value !== null && value !== undefined) {
        values.add(value);
      }
    }
  });

  // Warn if property wasn't found on any features
  if (propertyFoundCount === 0) {
    console.warn(
      `[Colors] Property "${property}" not found on any features. Available properties: ${
        features[0]?.properties ? Object.keys(features[0].properties).join(", ") : "none"
      }`
    );
  }

  return Array.from(values).sort();
};

/**
 * Maps unique values to categorical colors with looping
 *
 * @param values - Array of unique values to map
 * @returns Map of value to color (hex string)
 */
export const mapValuesToCategoricalColors = (values: (string | number)[]): Record<string | number, string> => {
  const colors = getThemeCategoricalColors();
  const mapping: Record<string | number, string> = {};

  values.forEach((value, index) => {
    // Loop through colors if we have more values than colors
    mapping[value] = colors[index % colors.length];
  });

  return mapping;
};

/**
 * Creates a categorical color mapping for use with vector layers or explicit mappings
 *
 * This is a helper for cases where categorical auto-assignment doesn't work:
 * - Vector tile layers (data not accessible at render time)
 * - URL-based GeoJSON layers
 * - Custom pin layers
 *
 * @param property - The property name to color by (e.g., "type", "category")
 * @param values - Array of known values for this property
 * @returns ColorSpec with explicit mapping
 *
 * @example
 * // Vector layer with categorical coloring
 * layer.vector({
 *   tileset: "mapbox://my-tileset",
 *   sourceLayer: "buildings",
 *   style: {
 *     color: createCategoricalMapping("type", ["residential", "commercial", "industrial"])
 *   }
 * })
 *
 * @example
 * // With custom colors for specific values
 * const mapping = createCategoricalMapping("status", ["active", "pending", "inactive"]);
 * // Then override specific colors if needed:
 * mapping.mapping.inactive = { hex: "#cccccc" };
 */
export const createCategoricalMapping = (
  property: string,
  values: (string | number)[]
): { by: string; mapping: Record<string, { hex: string }> } => {
  const colorMap = mapValuesToCategoricalColors(values);

  const mapping: Record<string, { hex: string }> = {};
  Object.entries(colorMap).forEach(([value, color]) => {
    mapping[value] = { hex: color };
  });

  return {
    by: property,
    mapping,
  };
};

// ============================================================================
// Sequential & Diverging Palette Utilities (Phase 2)
// ============================================================================

/**
 * Extract numeric values from GeoJSON data for sequential/diverging palettes
 *
 * @param data - GeoJSON FeatureCollection or Feature
 * @param property - Property name to extract values from
 * @returns Sorted array of numeric values
 */
export const extractNumericValues = (data: GeoJSON.FeatureCollection | GeoJSON.Feature, property: string): number[] => {
  if (!data || typeof data !== "object") {
    console.warn("[Colors] Invalid GeoJSON data provided to extractNumericValues");
    return [];
  }

  if (!("type" in data) || (data.type !== "FeatureCollection" && data.type !== "Feature")) {
    console.warn("[Colors] Data is not valid GeoJSON (missing type or invalid type)");
    return [];
  }

  const features = "features" in data ? data.features : [data];

  if (!Array.isArray(features) || features.length === 0) {
    console.warn(`[Colors] No features found in GeoJSON data`);
    return [];
  }

  const values = new Set<number>();
  let propertyFoundCount = 0;

  features.forEach((feature) => {
    if (!feature || typeof feature !== "object") {
      return;
    }

    if (feature.properties && property in feature.properties) {
      propertyFoundCount++;
      const value = feature.properties[property];

      // Only accept actual numbers
      if (typeof value === "number" && !isNaN(value)) {
        values.add(value);
      }
    }
  });

  if (propertyFoundCount === 0) {
    console.warn(
      `[Colors] Property "${property}" not found on any features. Available properties: ${
        features[0]?.properties ? Object.keys(features[0].properties).join(", ") : "none"
      }`
    );
  }

  return Array.from(values).sort((a, b) => a - b);
};

/**
 * Calculate min and max bounds from numeric values
 *
 * @param values - Sorted array of numeric values
 * @returns Object with min and max, or {min: 0, max: 1} if empty
 */
export const calculateBounds = (values: number[]): { min: number; max: number } => {
  if (values.length === 0) {
    console.warn("[Colors] Cannot calculate bounds from empty array");
    return { min: 0, max: 1 };
  }

  return {
    min: values[0],
    max: values[values.length - 1],
  };
};

/**
 * Calculate center point for diverging palettes
 *
 * @param values - Sorted array of numeric values
 * @param method - "median", "mean", or explicit number
 * @returns Center value
 */
export const calculateCenter = (values: number[], method: "median" | "mean" | number): number => {
  // Explicit number provided
  if (typeof method === "number") {
    return method;
  }

  if (values.length === 0) {
    console.warn("[Colors] Cannot calculate center from empty array, defaulting to 0");
    return 0;
  }

  if (method === "median") {
    const mid = Math.floor(values.length / 2);
    return values.length % 2 === 0 ? (values[mid - 1] + values[mid]) / 2 : values[mid];
  }

  // method === "mean"
  return values.reduce((sum, v) => sum + v, 0) / values.length;
};

/**
 * Get colors for a specific sequential palette
 *
 * @param scheme - Sequential palette name (defaults to "viridis")
 * @returns Array of 10 hex color strings
 */
export const getSequentialColors = (scheme: string = "viridis"): string[] => {
  // Convert camelCase to kebab-case
  const paletteMap: Record<string, string> = {
    redYellowGreen: "red-yellow-green",
  };

  const kebabName = paletteMap[scheme] || scheme;
  const prefix = `viz-sequential-${kebabName}`;
  const colors: string[] = [];

  // Each sequential palette has 10 colors (1-10)
  for (let i = 1; i <= 10; i++) {
    const cssVar = `${prefix}-${i}`;
    const resolved = getResolvedColor(cssVar);
    colors.push(resolved);
  }

  return colors;
};

/**
 * Get colors for a specific diverging palette
 *
 * @param scheme - Diverging palette name (defaults to "redYellowBlue")
 * @returns Array of 10 hex color strings (color 5 is the center)
 */
export const getDivergingColors = (scheme: string = "redYellowBlue"): string[] => {
  // Convert camelCase to kebab-case
  const paletteMap: Record<string, string> = {
    orangeYellowSeafoam: "orange-yellow-seafoam",
    redYellowBlue: "red-yellow-blue",
    redBlue: "red-blue",
  };

  const kebabName = paletteMap[scheme] || scheme;
  const prefix = `viz-diverging-${kebabName}`;
  const colors: string[] = [];

  // Each diverging palette has 10 colors (1-10), with 5 being the center
  for (let i = 1; i <= 10; i++) {
    const cssVar = `${prefix}-${i}`;
    const resolved = getResolvedColor(cssVar);
    colors.push(resolved);
  }

  return colors;
};

/**
 * Get color from sequential scale for a given value
 * Uses existing sequential palettes to interpolate color based on value position in range
 *
 * @param value - The value to get color for
 * @param min - Minimum value in the range
 * @param max - Maximum value in the range
 * @param scheme - Sequential palette name (defaults to "viridis")
 * @returns Interpolated color string
 *
 * @example
 * // Get color for response time (0-5000ms range)
 * const color = getColorFromSequentialScale(2500, 0, 5000, 'viridis');
 */
export const getColorFromSequentialScale = (
  value: number,
  min: number,
  max: number,
  scheme: string = "viridis"
): string => {
  const colors = getSequentialColors(scheme);

  // Handle edge cases
  if (value <= min) return colors[0];
  if (value >= max) return colors[colors.length - 1];
  if (min === max) return colors[Math.floor(colors.length / 2)];

  // Calculate position in range (0 to 1)
  const position = (value - min) / (max - min);

  // Map to color index (0 to colors.length - 1)
  const colorIndex = position * (colors.length - 1);
  const lowerIndex = Math.floor(colorIndex);
  const upperIndex = Math.ceil(colorIndex);

  // If exact match, return that color
  if (lowerIndex === upperIndex) {
    return colors[lowerIndex];
  }

  // For now, return the lower color (simple implementation)
  // Could be enhanced with actual color interpolation in the future
  return colors[lowerIndex];
};

/**
 * Get color from diverging scale for a given value
 * Uses existing diverging palettes to interpolate color based on value position relative to center
 *
 * @param value - The value to get color for
 * @param min - Minimum value in the range
 * @param max - Maximum value in the range
 * @param center - The center point of the diverging scale
 * @param scheme - Diverging palette name (defaults to "redYellowBlue")
 * @returns Interpolated color string
 *
 * @example
 * // Get color for temperature anomaly (-10 to +10, center at 0)
 * const color = getColorFromDivergingScale(5, -10, 10, 0, 'redYellowBlue');
 */
export const getColorFromDivergingScale = (
  value: number,
  min: number,
  max: number,
  center: number,
  scheme: string = "redYellowBlue"
): string => {
  const colors = getDivergingColors(scheme);
  const centerIndex = 4; // 5th color (index 4) is the center

  // Handle edge cases
  if (value <= min) return colors[0];
  if (value >= max) return colors[colors.length - 1];
  if (value === center) return colors[centerIndex];
  if (min === max) return colors[centerIndex];

  // Determine which side of center we're on
  if (value < center) {
    // Left side: min to center (colors 0-4)
    const position = (value - min) / (center - min);
    const colorIndex = position * centerIndex;
    const lowerIndex = Math.floor(colorIndex);
    return colors[Math.min(lowerIndex, centerIndex)];
  } else {
    // Right side: center to max (colors 4-9)
    const position = (value - center) / (max - center);
    const colorIndex = centerIndex + position * (colors.length - 1 - centerIndex);
    const lowerIndex = Math.floor(colorIndex);
    return colors[Math.min(lowerIndex, colors.length - 1)];
  }
};

/**
 * Creates a sequential color mapping for vector layers or manual bounds
 *
 * Use this helper when categorical auto-assignment doesn't work:
 * - Vector tile layers (data not accessible at render time)
 * - URL-based GeoJSON layers
 * - When you want consistent colors across multiple maps
 *
 * @param property - The property name to color by
 * @param bounds - Min and max values for the scale
 * @param scheme - Sequential palette name (defaults to "viridis")
 * @returns Mapbox interpolate expression
 *
 * @example
 * // Vector layer with sequential coloring
 * layer.vector({
 *   tileset: "mapbox://my-tileset",
 *   sourceLayer: "population",
 *   style: {
 *     color: createSequentialMapping("density", { min: 0, max: 100000 }, "magma")
 *   }
 * })
 */
export const createSequentialMapping = (
  property: string,
  bounds: { min: number; max: number },
  scheme: string = "viridis"
): MapboxExpression => {
  const colors = getSequentialColors(scheme);
  const expr: MapboxExpression = ["interpolate", ["linear"], ["get", property]];

  // Guard against collapsed range (all values the same)
  // Mapbox requires at least 2 stops for interpolate, so use same color for min and a tiny offset
  if (bounds.min === bounds.max) {
    expr.push(bounds.min, colors[0]);
    expr.push(bounds.min + 0.0001, colors[0]);
    return expr;
  }

  colors.forEach((color, i) => {
    const t = i / (colors.length - 1); // 0 to 1
    const value = bounds.min + t * (bounds.max - bounds.min);
    expr.push(value, color);
  });

  return expr;
};

/**
 * Creates a diverging color mapping for vector layers or manual bounds
 *
 * Use this helper when categorical auto-assignment doesn't work:
 * - Vector tile layers (data not accessible at render time)
 * - URL-based GeoJSON layers
 * - When you want consistent colors across multiple maps
 *
 * @param property - The property name to color by
 * @param bounds - Min and max values for the scale
 * @param center - The center point (e.g., 0 for average, sea level, break-even)
 * @param scheme - Diverging palette name (defaults to "redYellowBlue")
 * @returns Mapbox interpolate expression
 *
 * @example
 * // Vector layer with diverging coloring
 * layer.vector({
 *   tileset: "mapbox://my-tileset",
 *   sourceLayer: "temperature",
 *   style: {
 *     color: createDivergingMapping("temp_anomaly", { min: -10, max: 10 }, 0, "orangeYellowSeafoam")
 *   }
 * })
 */
export const createDivergingMapping = (
  property: string,
  bounds: { min: number; max: number },
  center: number,
  scheme: string = "redYellowBlue"
): MapboxExpression => {
  const colors = getDivergingColors(scheme);
  const expr: MapboxExpression = ["interpolate", ["linear"], ["get", property]];
  const centerIndex = 4; // 5th color (index 4) is the center

  // Guard against collapsed range (all values the same)
  // Mapbox requires at least 2 stops for interpolate, so use same color for min and a tiny offset
  if (bounds.min === bounds.max) {
    expr.push(bounds.min, colors[centerIndex]);
    expr.push(bounds.min + 0.0001, colors[centerIndex]);
    return expr;
  }

  // Handle center at min edge
  if (center === bounds.min) {
    expr.push(center, colors[centerIndex]);
    // Only add stops from center to max
    for (let i = centerIndex + 1; i < colors.length; i++) {
      const t = (i - centerIndex) / (colors.length - 1 - centerIndex);
      const value = center + t * (bounds.max - center);
      expr.push(value, colors[i]);
    }
    return expr;
  }

  // Handle center at max edge
  if (center === bounds.max) {
    // Only add stops from min to center
    for (let i = 0; i < centerIndex; i++) {
      const t = i / centerIndex;
      const value = bounds.min + t * (center - bounds.min);
      expr.push(value, colors[i]);
    }
    expr.push(center, colors[centerIndex]);
    return expr;
  }

  // Normal case: center is between min and max
  // Colors 1-5: min → center
  for (let i = 0; i <= centerIndex; i++) {
    const t = i / centerIndex; // 0 to 1
    const value = bounds.min + t * (center - bounds.min);
    expr.push(value, colors[i]);
  }

  // Colors 6-10: center → max
  for (let i = centerIndex + 1; i < colors.length; i++) {
    const t = (i - centerIndex) / (colors.length - 1 - centerIndex); // 0 to 1
    const value = center + t * (bounds.max - center);
    expr.push(value, colors[i]);
  }

  return expr;
};
